import { bopApi } from '@/lib/api/client';
import { parseDateInputToUtc } from '@/utils/date';

const FEES_CHARGED_PATH = 'v1/fees/getcharged';

/** Suffix marking the server-derived EUR-converted amount for a currency. */
export const EUR_KEY_SUFFIX = '-EUR';

export interface GetFeesChargedRequest {
  /** ISO datetime; null/omitted → server default (now − 24h) */
  from?: string | null;
  /** ISO datetime; null/omitted → server default (now) */
  to?: string | null;
}

/**
 * One record per fee-collection run. Beyond the Dynamo keys, every field is a
 * currency code holding the RAW collected amount, plus a `${currency}-EUR`
 * derived value (8 dp) for each currency crypto-info could price. Currencies
 * WITHOUT a price get no -EUR key and are silently excluded from chart, table
 * and profit downstream (kept, matches the legacy profit definition).
 */
export interface CollectedFeeRecord {
  p: 'collected-fees';
  /** sort key — epoch-milliseconds STRING (the collection-run timestamp) */
  s: string;
  /** currency → raw amount, `${currency}-EUR` → EUR value (may arrive as a numeric string) */
  [currencyOrDerived: string]: number | string;
}

export type GetFeesChargedResponse = CollectedFeeRecord[];

/** Range as held by the date inputs — plain YYYY-MM-DD strings ('' = unset → server default). */
export interface FeesDateRange {
  from: string;
  to: string;
}

// Day boundaries are UTC regardless of the operator's locale — the records' sort
// keys are UTC epochs and legacy numbers must reproduce.
// Deliberately kept local rather than moved to utils/date.ts: this Date-in/Date-out
// rounding pair exists only for this endpoint's epoch-keyed records — the shared
// string-based day-boundary helpers live in utils/date.ts.
export const roundDownToStartOfDayUtc = (date: string | number | Date): Date => {
  const result = new Date(date);
  result.setUTCHours(0, 0, 0, 0);
  return result;
};

export const roundUpToEndOfDayUtc = (date: string | number | Date): Date => {
  const result = new Date(date);
  result.setUTCHours(23, 59, 59, 999);
  return result;
};

/**
 * UTC day-bound rounding moved client-side from the Nuxt route.
 * Deliberate fix: the lambda's date validation is broken
 * (`Number.isNaN(dateObject)` never fires) and an unparseable date silently
 * yields an empty result — invalid/empty inputs are sent as null (server
 * defaults) instead of ever reaching the wire unparsed.
 */
export const buildFeesChargedRequest = ({ from, to }: FeesDateRange): GetFeesChargedRequest => {
  const fromDate = parseDateInputToUtc(from);
  const toDate = parseDateInputToUtc(to);
  return {
    from: fromDate ? roundDownToStartOfDayUtc(fromDate).toISOString() : null,
    to: toDate ? roundUpToEndOfDayUtc(toDate).toISOString() : null,
  };
};

export const getFeesCharged = (body: GetFeesChargedRequest) => bopApi.post(FEES_CHARGED_PATH, { json: body }).json<GetFeesChargedResponse>();

/** One chart row per UTC day: `date` plus one EUR-value field per base currency code. */
export interface FeesChartRow {
  [seriesKey: string]: number | string;
  /** YYYY-MM-DD (UTC day bucket) */
  date: string;
}

/** One table row per UTC day: `date`, plus per currency the RAW amount (`BTC`) and EUR amount (`BTC-EUR`). */
export interface FeesTableRow {
  [currencyField: string]: number | string;
  date: string;
}

export interface FeesView {
  /** Sum of every per-record `-EUR` value — each pre-rounded to 8 dp server-side, summed as-is so totals match legacy */
  profitRaw: number;
  /** Base currency codes in key-discovery order — drives chart series + color assignment (legacy data-dependent order kept) */
  chartSeries: string[];
  /** Base currency codes sorted alphabetically — drives the table column order */
  tableCurrencies: string[];
  chartData: FeesChartRow[];
  tableRows: FeesTableRow[];
}

/**
 * The per-day-per-currency pivot moved from the legacy page:
 * profit, stacked-bar chart rows and dynamic-column table rows from one pass
 * over the raw records. Pure — used as a stable TanStack Query select.
 */
export const buildFeesView = (fees: CollectedFeeRecord[]): FeesView => {
  // Profit: sum every `-EUR` key across all records. Number() guards the
  // numeric-string case without changing well-formed sums.
  let profitRaw = 0;
  for (const record of fees) {
    for (const [key, value] of Object.entries(record)) {
      if (key.includes(EUR_KEY_SUFFIX)) profitRaw += Number(value);
    }
  }

  // Group records into UTC-day buckets, summing every non-key field.
  const groupedByDate: Record<string, Record<string, number>> = {};
  for (const fee of fees) {
    const date = new Date(Number.parseInt(fee.s, 10)).toISOString().split('T')[0];
    groupedByDate[date] ??= {};
    for (const [key, value] of Object.entries(fee)) {
      if (key === 's' || key === 'p') continue;
      // Deliberate fix: legacy `+=` would concatenate a numeric STRING
      groupedByDate[date][key] = (groupedByDate[date][key] ?? 0) + Number(value);
    }
  }

  // Series = ONLY the -EUR keys, discovered across all records in insertion order.
  const uniqueCurrencies = [...new Set(fees.flatMap((fee) => Object.keys(fee).filter((key) => key !== 's' && key !== 'p' && key.includes(EUR_KEY_SUFFIX))))];

  // Sorted date labels.
  const labels = Object.keys(groupedByDate).sort();

  const chartSeries = uniqueCurrencies.map((currency) => currency.replace(EUR_KEY_SUFFIX, ''));

  // One row per day, one numeric field per currency (EUR values).
  const chartData: FeesChartRow[] = labels.map((date) => ({
    date,
    ...Object.fromEntries(uniqueCurrencies.map((currency) => [currency.replace(EUR_KEY_SUFFIX, ''), groupedByDate[date][currency] ?? 0])),
  }));

  // Table rows carry the RAW native amount AND the EUR amount per currency.
  const tableRows: FeesTableRow[] = labels.map((date) => {
    const row: FeesTableRow = { date };
    for (const currency of uniqueCurrencies) {
      const base = currency.replace(EUR_KEY_SUFFIX, '');
      row[base] = groupedByDate[date][base] ?? 0;
      row[`${base}${EUR_KEY_SUFFIX}`] = groupedByDate[date][currency] ?? 0;
    }
    return row;
  });

  // Table columns sorted alphabetically by currency code (legacy sorted its column config)
  const tableCurrencies = [...chartSeries].sort();

  return { profitRaw, chartSeries, tableCurrencies, chartData, tableRows };
};
